import React, { useEffect } from 'react';
import styled, { css } from 'styled-components';
import { useEditMode } from '../shared/hooks/useEditMode';
import { useSetting } from '../shared/hooks/useApi/useSetting';
import { AlertPosition, AlertType, ModalType, NavLayout } from '../shared/enum/EnumRepository';
import { useModalStore } from '../shared/store/portal/modalStore';
import { getHintMessage } from '../shared/constants/hintMessages';
import { getAlertMessage } from '../shared/constants/alertMessages';
import { usePageOwnerIdentityStore } from '../shared/store/pageOwnerIdentityStore';
import { useStandardAlertStore } from '../shared/store/portal/alertStore';
import { isExposureLevelSearchable } from '../shared/util/exposureLevel';
import MemberSetting from '../component/setting/MemberSetting';
import PageSetting from '../component/setting/PageSetting';
import Subscription from '../component/setting/Subscription';
import Notification from '../component/setting/Notification';
import ConnectionManagement from '../component/setting/ConnectionManagement';
import HintWrapper from '../shared/component/headless/button/HintWrapper';
import UniversalIconBtn from '../shared/component/headless/button/UniversalIconBtn';
import ArchivePageFrame from '../shared/component/ArchivePageFrame';
import qrCodeIcon from '../asset/icon/qr_code_icon.png';
import { useNavLayout } from '../shared/hooks/useNavLayout';

const Settings: React.FC = () => {
  const { setEditMode } = useEditMode();
  const { setting } = useSetting();
  const pageOwner = usePageOwnerIdentityStore(s => s.pageOwner);
  const { enqueueAlert } = useStandardAlertStore();
  const { setStandardModal } = useModalStore();
  const navLayout = useNavLayout();

  useEffect(() => {
    setEditMode(true);
  }, [setEditMode]);

  const handleGenerateQRCode = () => {
    if (!pageOwner || !setting) {
      return;
    }
    if (!isArchiveSearchable) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('QR_CODES_PUBLIC_ONLY'),
      });
      return;
    }
    setStandardModal({
      modalType: ModalType.QR_GENERATOR,
      title: "QR Code Generator",
      value: "",
      handleChange: () => { },
    });
  }

  const isArchiveSearchable = setting ? isExposureLevelSearchable(setting.exposureLevel) : false;

  return (
    <SettingsContainer $isFold={navLayout === NavLayout.FOLD}>
      <MemberSetting />
      <PageSetting />
      <Subscription />
      <ConnectionManagement />
      <Notification />
      <BtnContainer>
        <HintWrapper
          content={getHintMessage('GENERATE_QR_CODE')}
          toLeft={true}
        >
          <UniversalIconBtn
            size="fit"
            border={true}
            backgroundImg={qrCodeIcon}
            onClick={handleGenerateQRCode}
          />
        </HintWrapper>
      </BtnContainer>
    </SettingsContainer>
  );
}

export default Settings;

const SettingsContainer = styled(ArchivePageFrame)<{ $isFold: boolean }>`
  ${({ $isFold }) => $isFold && css`
    && {
      gap: 30px;
      padding: calc(10vh) calc(6vw) calc(8vh);
    }

    && > section {
      width: clamp(320px, 65%, 690px);
      margin: 0;
    }
  `}
`;

const BtnContainer = styled.div`
  position: fixed;
  right: clamp(20px, 2vw, 30px);
  bottom: calc(8vh);
  width: fit-content;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.5vw;
`
